import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserWatchlists, Watchlist_WatchlistItem, Watchlist_Type, Watchlist_ShareType } from "../proto/generated/watchlist";

export type WatchlistItemData = {
    id: string,
    item: Watchlist_WatchlistItem,
}

export type Watchlist = {
    id: string;
    name: string;
    watchlistItems: WatchlistItemData[];
    default: boolean;
    type: Watchlist_Type[];
    shareType: Watchlist_ShareType;
    shareId: string;
    screenerId?: string;
    groupView?: boolean;
}
export interface WatchlistStateType {
    watchlists: Record<string, Watchlist>
    userWatchlists: UserWatchlists,
    communityWatchlist: UserWatchlists,
    addOrRemoveWatchlistItem: {
        watchlistItem: Watchlist_WatchlistItem,
        selected: boolean,
        watchlistId: string,
        showHighlighted: boolean
    } | undefined,
    preview: {
        isActive: boolean;
        watchlistId?: string;
        shareId?: string;
        name?: string;
        data?: Watchlist | null;
    };
}

const initialState: WatchlistStateType = {
    watchlists: {},
    userWatchlists: { watchlists: [] },
    communityWatchlist: { watchlists: [] },
    addOrRemoveWatchlistItem: undefined,
    preview: {
        isActive: false,
        watchlistId: undefined,
        shareId: null,
        name: null,
        data: null,
    },
};

const watchlistSlice = createSlice({
    name: 'watchlist',
    initialState,
    reducers: {
        setWatchlists(state: WatchlistStateType, action: PayloadAction<Record<string, Watchlist>>) {
            const { payload } = action;
            return {
                ...state,
                watchlists: { ...payload },
                watchlistIdToInfo: Object.keys(payload).map(key => [key, payload[key]])
            }
        },
        renameWatchlistSectionSuccess: (
            state,
            action: PayloadAction<{ watchlistId: string; sectionId: string; newName: string }>
        ) => {
            const { watchlistId, sectionId, newName } = action.payload;

            const watchlist = state.watchlists[watchlistId];
            if (!watchlist) return;

            watchlist.watchlistItems = watchlist.watchlistItems.map(item =>
                item.id === sectionId
                    ? { ...item, item: { ...item.item, sectionName: newName } }
                    : item
            );
        },

        saveUserWatchlists(state: WatchlistStateType, action: PayloadAction<UserWatchlists>) {
            return {
                ...state,
                userWatchlists: action.payload
            }
        },
        setAddRemoveWatchlistItem(state: WatchlistStateType, action: PayloadAction<WatchlistStateType['addOrRemoveWatchlistItem']>) {
            return {
                ...state,
                addOrRemoveWatchlistItem: action.payload
            }
        },
        removewatchlistItem(state: WatchlistStateType, action: PayloadAction<{
            itemId: number[]
            watchlistId: string
        }>) {
            const { itemId, watchlistId } = action.payload
            const newList = { ...state.watchlists[watchlistId] }
            newList.watchlistItems = newList.watchlistItems.filter(i => !itemId.includes(i.item.id))
            return {
                ...state,
                watchlists: {
                    ...state.watchlists,
                    [watchlistId]: newList
                }
            }
        },
        setCommunityWatchlists(state: WatchlistStateType, action: PayloadAction<UserWatchlists>) {
            return {
                ...state,
                communityWatchlist: action.payload
            }
        },
        setDefaultWatchlist(state: WatchlistStateType, action: PayloadAction<string>) {
            const watchlistId = action.payload;
            Object.keys(state.watchlists).forEach(id => {
                state.watchlists[id].default = (id === watchlistId);
            });
        },
        setWatchlist(state: WatchlistStateType, action: PayloadAction<Watchlist>) {
            const watchlist = action.payload;
            state.watchlists[watchlist.id] = watchlist;
        },
        setPreviewWatchlistId(
            state: WatchlistStateType,
            action: PayloadAction<{ isActive: boolean; watchlistId?: string; shareId?: string; name?: string }>
        ) {
            state.preview.isActive = action.payload.isActive;
            state.preview.watchlistId = action.payload.watchlistId;
            state.preview.shareId = action.payload.shareId;
            state.preview.name = action.payload.name;
        },

        setPreviewWatchlist(
            state: WatchlistStateType,
            action: PayloadAction<Watchlist>
        ) {
            state.preview.data = action.payload;
        },

        clearPreviewWatchlist(state: WatchlistStateType) {
            state.preview = {
                isActive: false,
                watchlistId: undefined,
                shareId: null,
                data: null,
            };
        },
    }
})


const { actions, reducer } = watchlistSlice

export const { setWatchlists, saveUserWatchlists, setAddRemoveWatchlistItem, removewatchlistItem, setCommunityWatchlists, setDefaultWatchlist, renameWatchlistSectionSuccess, setWatchlist, setPreviewWatchlistId, clearPreviewWatchlist, setPreviewWatchlist} = actions

export default reducer
    